'use client';

import { useEffect, useSyncExternalStore } from 'react';

import { getDictionary } from '@/features/i18n/dictionaries';
import {
    DEFAULT_LOCALE,
    getIntlLocale,
    getLocaleDirection,
    LOCALE_COOKIE_KEY,
    LOCALE_STORAGE_KEY,
    normalizeLocale,
    type SupportedLocale,
} from '@/features/i18n/logic';
import { recoverChunkLoadFailure } from '@/features/runtime/chunkRecovery';
import { reportClientError } from '@/lib/observability/clientRollbar';

const readLocaleFromBrowser = (): SupportedLocale => {
    if (typeof document === 'undefined') {
        return DEFAULT_LOCALE;
    }

    const encodedPattern = `(?:^|; )${LOCALE_COOKIE_KEY}=([^;]*)`;
    const match = document.cookie.match(new RegExp(encodedPattern));

    if (match?.[1]) {
        try {
            return normalizeLocale(decodeURIComponent(match[1]));
        } catch {
            return normalizeLocale(match[1]);
        }
    }

    try {
        return normalizeLocale(window.localStorage.getItem(LOCALE_STORAGE_KEY));
    } catch {
        return DEFAULT_LOCALE;
    }
};

const noopSubscribe = () => () => {};

const ErrorPage = ({ error, reset }: { error: Error & { digest?: string }; reset: () => void }) => {
    const locale = useSyncExternalStore(noopSubscribe, readLocaleFromBrowser, () => DEFAULT_LOCALE);

    const dictionary = getDictionary(locale);
    const dir = getLocaleDirection(locale);
    const lang = getIntlLocale(locale);

    useEffect(() => {
        const didRecover = recoverChunkLoadFailure({
            error,
            buildMarker: process.env.NEXT_PUBLIC_CHATTENGER_BUILD_MARKER,
            browserWindow: typeof window === 'undefined' ? null : window,
        });

        if (didRecover) {
            return;
        }

        reportClientError({
            error,
            extra: {
                digest: error.digest || null,
                boundary: 'error.tsx',
            },
        });
    }, [error]);

    const handleRetry = () => {
        reset();
    };

    return (
        <div
            dir={dir}
            lang={lang}
            style={{
                minHeight: '100vh',
                display: 'grid',
                placeItems: 'center',
                padding: '32px',
                textAlign: 'center',
                background: 'linear-gradient(180deg, #080b10 0%, #131922 100%)',
                color: '#f5f7fb',
            }}
            data-testid="error-div"
        >
            <div style={{ display: 'grid', gap: '16px', maxWidth: '420px' }} data-testid="error-div-2">
                <h1 style={{ margin: 0, fontSize: '2rem' }} data-testid="error-title">
                    {dictionary.appError.title}
                </h1>
                <p style={{ margin: 0, opacity: 0.78 }} data-testid="error-description">
                    {dictionary.appError.description}
                </p>
                <button
                    onClick={handleRetry}
                    style={{
                        appearance: 'none',
                        border: '1px solid rgba(226, 232, 240, 0.14)',
                        borderRadius: '999px',
                        padding: '12px 18px',
                        background: 'rgba(229, 236, 245, 0.12)',
                        color: 'inherit',
                        cursor: 'pointer',
                    }}
                    type="button"
                    data-testid="error-button"
                >
                    {dictionary.appError.retry}
                </button>
            </div>
        </div>
    );
};

export default ErrorPage;
